import { ref } from 'vue'

import type { ComponentNode, ComponentNodeMultimediaThemeMeta, MultimediaProps } from '@canvas-builder/types/canvas'
import type { CanvasRetrieve } from '@openapi'
import type { CanvasThemeRetrieve } from '@lib/types/canvasTheme'

// Module-level state (singleton pattern)
const processedNodes = ref(new Set<string>())
const manuallyChangedThemeAssets = ref(new Set<string>())

function resetProcessedNodes() {
  processedNodes.value.clear()
}

function addManuallyChangedThemeAsset(componentId: string) {
  manuallyChangedThemeAssets.value.add(componentId)
}

function removeManuallyChangedThemeAsset(componentId: string) {
  manuallyChangedThemeAssets.value.delete(componentId)
}

function isManuallyChangedThemeAsset(componentId: string): boolean {
  return manuallyChangedThemeAssets.value.has(componentId)
}

function clearManuallyChangedThemeAssets() {
  manuallyChangedThemeAssets.value.clear()
}

function processThemeAssetForNode(
  node: ComponentNode,
  theme: CanvasThemeRetrieve,
  updateNodeFn: (
    nodeId: string,
    props: MultimediaProps,
    directNodeUpdate?: {
      node: ComponentNode
      section: CanvasRetrieve['sections'][number]
    },
  ) => boolean,
  section?: CanvasRetrieve['sections'][number],
): boolean {
  // Skip if not a multimedia component or already processed
  if (node.type !== 'Multimedia' || !node.theme_meta || processedNodes.value.has(node.id)) {
    return false
  }

  const { is_theme_logo, theme_asset_index } = node.theme_meta as ComponentNodeMultimediaThemeMeta
  let updated = false

  // Process logo theme asset
  if (is_theme_logo) {
    processedNodes.value.add(node.id)

    if (theme.logo) {
      // Update with theme logo
      updated = updateNodeFn(
        node.id,
        {
          asset_type: 'pitcher_asset',
          file: {
            id: theme.logo.id,
            name: theme.logo.name || 'Theme Logo',
            content_url: theme.logo.url,
          },
          type: 'image',
          horizontal_alignment: (node.data as MultimediaProps)?.horizontal_alignment,
        },
        section ? { node, section } : undefined,
      )
    } else {
      // Clear the logo if none exists
      updated = updateNodeFn(node.id, { type: 'image' } as MultimediaProps, section ? { node, section } : undefined)
    }
  }

  // Process regular theme assets
  if (theme_asset_index !== undefined) {
    processedNodes.value.add(node.id)

    if (theme.assets && theme.assets.length > 0) {
      const assetIndex = Math.min(theme_asset_index, theme.assets.length - 1)
      if (assetIndex >= 0) {
        const asset = theme.assets[assetIndex]
        updated = updateNodeFn(
          node.id,
          {
            asset_type: 'pitcher_asset',
            file: {
              id: asset.id,
              name: asset.name || `Theme Asset ${assetIndex + 1}`,
              content_url: asset.url,
            },
            type: 'image',
            horizontal_alignment: (node.data as MultimediaProps)?.horizontal_alignment,
          },
          section ? { node, section } : undefined,
        )
      }
    } else {
      // Clear the asset if none exists
      updated = updateNodeFn(node.id, { type: 'image' } as MultimediaProps, section ? { node, section } : undefined)
    }
  }

  return updated
}

function processThemeAssetsForNodes(
  nodes: ComponentNode[],
  theme: CanvasThemeRetrieve,
  updateNodeFn: (
    nodeId: string,
    props: MultimediaProps,
    directNodeUpdate?: {
      node: ComponentNode
      section: CanvasRetrieve['sections'][number]
    },
  ) => boolean,
  section?: CanvasRetrieve['sections'][number],
): number {
  if (!theme) {
    return 0
  }

  let updatedCount = 0

  // Reset processed nodes tracking
  resetProcessedNodes()

  const processNodes = (nodesToProcess: ComponentNode[]) => {
    nodesToProcess.forEach((node) => {
      // Process the current node
      const wasUpdated = processThemeAssetForNode(node, theme, updateNodeFn, section)
      if (wasUpdated) {
        updatedCount++
      }

      // Process children recursively
      if (node.children?.length) {
        processNodes(node.children)
      }
    })
  }

  processNodes(nodes)
  return updatedCount
}

function processThemeAssetsForSections(
  sections: CanvasRetrieve['sections'],
  updateNodeFn: (
    nodeId: string,
    props: MultimediaProps,
    directNodeUpdate?: {
      node: ComponentNode
      section: CanvasRetrieve['sections'][number]
    },
  ) => boolean,
  specificSection?: CanvasRetrieve['sections'][number],
): number {
  if (!sections || !sections.length || !updateNodeFn) {
    return 0
  }

  let updatedCount = 0

  // Reset processed nodes tracking
  resetProcessedNodes()

  // Process each section that has its own theme
  sections.forEach((section: CanvasRetrieve['sections'][number]) => {
    // Skip sections that inherit theme from parent or have no theme/content
    if (!section.theme || section.theme?.inherit_theme_from_parent) return
    if (!section.content?.data?.length) return

    // If a specific section is provided, only process that one
    if (specificSection && specificSection.id !== section.id) return

    // Start processing from the root nodes of the section
    const sectionUpdates = processThemeAssetsForNodes(
      section.content.data,
      section.theme as CanvasThemeRetrieve,
      updateNodeFn,
      section,
    )

    updatedCount += sectionUpdates
  })

  return updatedCount
}

export default function useCanvasThemeAssets() {
  return {
    processThemeAssetForNode,
    processThemeAssetsForNodes,
    processThemeAssetsForSections,
    resetProcessedNodes,
    addManuallyChangedThemeAsset,
    removeManuallyChangedThemeAsset,
    isManuallyChangedThemeAsset,
    clearManuallyChangedThemeAssets,
  }
}
